import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  ProgressBar,
  Spinner,
} from '@/components/ui';
import { formatFixed } from '@/logic/statistics';
import { useProgressStore } from '@/store/progressStore';

/** Work shorter than this never opens the blocking dialog. */
export const PROCESSING_DIALOG_DELAY_MS = 500;

interface ProcessingOverlayProps {
  onCancel: () => void;
}

/**
 * Feedback for worker calculations. A slim progress bar appears at the top
 * of the viewport immediately; a modal with progress and Cancel opens only
 * if the work is still running after `PROCESSING_DIALOG_DELAY_MS`, so quick
 * solves never flash a dialog. Escape or Cancel stops the worker.
 *
 * Subscribes to worker progress itself so progress ticks re-render only this.
 */
export const ProcessingOverlay = ({ onCancel }: ProcessingOverlayProps) => {
  const isProcessing = useProgressStore((s) => s.isProcessing);
  const progress = useProgressStore((s) => s.progress);
  const message = useProgressStore((s) => s.message);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Where focus was when the dialog opened, to return it afterwards.
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isProcessing) {
      setDialogOpen(false);
      return;
    }
    const timer = window.setTimeout(() => {
      returnFocus.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      setDialogOpen(true);
    }, PROCESSING_DIALOG_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isProcessing]);

  if (!isProcessing) return null;
  const determinate = progress > 0;

  return (
    <>
      <ProgressBar
        variant="top"
        aria-label="Calculation progress"
        value={determinate ? progress : undefined}
      />
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) onCancel();
        }}
      >
        <DialogContent
          size="sm"
          title="Working…"
          description="Calculations run in a background thread. Cancelling leaves your data unchanged."
          showClose={false}
          // A stray click outside must not cancel a long calculation.
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          // Return focus only if nothing else claimed it: a finished trial
          // focuses its own review, which must not be pulled back.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const active = document.activeElement;
            if (!active || active === document.body)
              returnFocus.current?.focus();
            returnFocus.current = null;
          }}
        >
          <DialogBody className="space-y-2.5">
            <div className="flex items-center gap-2.5">
              <Spinner className="text-accent" />
              <span
                role="status"
                className="min-w-0 flex-1 truncate text-sm text-fg"
              >
                {message || 'Calculating'}
              </span>
              {determinate && (
                <span className="font-mono text-xs tabular-nums text-fg-muted">
                  {formatFixed(Math.min(progress, 100), 0)}%
                </span>
              )}
            </div>
            <ProgressBar
              aria-label="Calculation progress detail"
              value={determinate ? progress : undefined}
            />
          </DialogBody>
          <DialogFooter>
            <Button onClick={onCancel} aria-label="Cancel calculation">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
